import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { missingLinkError } from '@execonline-inc/links.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../Appy';
import { findLinkBy } from '../../../../../../Links';
import ReactionComponent from '../../../../../../ReactionComponent';
import { Link } from '../../../../../../Resource/Types';
import { learningCollectionSummaryResourceDecoder } from '../../../../Decoders';
import { LearningCollectionResource } from '../../../../Types';
import LearningCollectionEditStore from '../../../Edit/Store';
import PublishingStore from './Store';
import { PublishingError, PublishingState, isPublished } from './Types';

interface Props {
  store: PublishingStore;
  learningCollectionResource: LearningCollectionResource;
  learningCollectionStore: LearningCollectionEditStore;
}

interface LearningCollectionPublishingOut {
  published: boolean;
}

const togglePublishStatus = (publishedAt: StructuredMaybe<Date>): boolean =>
  !isPublished(publishedAt);

export const learningCollectionPublishingEncoder = (
  publishedAt: StructuredMaybe<Date>,
): LearningCollectionPublishingOut => ({
  published: togglePublishStatus(publishedAt),
});

const handleReportsError = (store: PublishingStore) => (error: PublishingError) => {
  store.error(error);
  warnAndNotify('PublishingReactions', error.kind, error);
};

const loadNewCollectionResource = (link: Maybe<Link>, publishedAt: StructuredMaybe<Date>) =>
  toTask<PublishingError, Link>(missingLinkError({ rel: 'publish' }), link).andThen(
    callApi(
      learningCollectionSummaryResourceDecoder,
      learningCollectionPublishingEncoder(publishedAt),
    ),
  );

const loadForNew = (
  link: Maybe<Link>,
  store: PublishingStore,
  publishedAt: StructuredMaybe<Date>,
  learningCollectionResource: LearningCollectionResource,
  learningCollectionStore: LearningCollectionEditStore,
) =>
  Task.succeed<PublishingError, {}>({})
    .assign('resource', loadNewCollectionResource(link, publishedAt))
    .fork(handleReportsError(store), ({ resource }) => {
      store.ready(findLinkBy({ rel: 'publish' }, resource.links), learningCollectionResource),
        learningCollectionStore.loadingForDeepLinks(String(resource.payload.id));
    });

class PublishingReactions extends ReactionComponent<PublishingStore, PublishingState, Props> {
  tester = () => this.props.store.state;
  effect = (state: PublishingState) => {
    const { store, learningCollectionResource, learningCollectionStore } = this.props;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
        break;
      case 'publishing':
        loadForNew(
          state.publishLink,
          store,
          state.publishing.payload.publishedAt,
          learningCollectionResource,
          learningCollectionStore,
        );
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default PublishingReactions;
